import clsx from 'clsx';
import { AlertTriangle, Coffee, Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { ApiError } from '../lib/api.js';

export function Spinner({ className }: { readonly className?: string | undefined }): ReactNode {
  return (
    <Loader2 aria-hidden="true" className={clsx('size-5 animate-spin text-ink-muted', className)} />
  );
}

export function LoadingBlock({ label = 'Carregando…' }: { readonly label?: string }): ReactNode {
  return (
    <div
      role="status"
      className="flex items-center justify-center gap-2 py-10 text-sm text-ink-muted"
    >
      <Spinner />
      {label}
    </div>
  );
}

/**
 * Estado vazio: um cartão de verdade, não uma moldura tracejada.
 *
 * A borda tracejada é a convenção de "aqui falta algo" — e fila vazia **não** é falta
 * de nada, é um dia calmo. O protótipo usa o mesmo cartão do resto da página, com o
 * ícone num disco teal, e o efeito é exatamente o oposto: a tela parece completa.
 *
 * O ícone é uma caneca, não uma caixa de entrada. Também deliberado: diz "pausa", e
 * não "nada aqui".
 */
export function EmptyState({
  title,
  description,
  action,
}: {
  readonly title: string;
  readonly description?: string | undefined;
  readonly action?: ReactNode | undefined;
}): ReactNode {
  return (
    <div className="anim-enter flex flex-col items-center gap-2.5 rounded-[var(--radius-card)] border border-line-card bg-linear-to-b from-white to-[#fdfefe] px-7 py-11 text-center shadow-[var(--shadow-card)]">
      <span className="mb-1 flex size-13 items-center justify-center rounded-full bg-brand-secondary/7 text-brand-secondary">
        <Coffee aria-hidden="true" className="size-5.5" strokeWidth={2} />
      </span>
      <p className="font-display text-base font-bold">{title}</p>
      {description === undefined ? null : (
        <p className="max-w-105 text-[0.78rem] leading-relaxed text-ink-muted text-pretty">
          {description}
        </p>
      )}
      {action}
    </div>
  );
}

/**
 * Mensagens de erro por código estável da API.
 *
 * **"Algo deu errado" é proibido** — Fundações §5.9.2 rejeitou por escrito. Todo
 * erro que chega ao usuário precisa dizer o que houve; código desconhecido ainda
 * mostra o código, que é mais útil que um genérico.
 */
const MESSAGES: Record<string, string> = {
  authentication_required: 'Sua sessão expirou. Entre novamente.',
  insufficient_role: 'Seu perfil não tem acesso a esta área.',
  csrf_token_invalid: 'Sessão desatualizada. Recarregue a página e tente de novo.',
  invalid_credentials: 'E-mail ou senha incorretos.',
  queue_entry_not_found: 'Este paciente não está mais na fila. A tela será atualizada.',
  call_gate_active: 'Outro paciente acabou de ser chamado. Aguarde alguns segundos.',
  unsupported_audio_type: 'Formato não aceito. Envie MP3, WAV, OGG ou M4A.',
  empty_audio: 'O arquivo chegou vazio. Tente enviar de novo.',
  invalid_transition_from_waiting: 'Este paciente ainda não foi chamado.',
  invalid_transition_from_calling: 'Este paciente está sendo chamado, não em atendimento.',
  invalid_transition_from_inService: 'Este paciente já está em atendimento.',
  invalid_transition_from_finished: 'Este atendimento já foi finalizado.',
  invalid_transition_from_withdrawn: 'Este paciente saiu da fila.',
  room_name_already_exists: 'Já existe uma sala com este nome.',
  room_not_found: 'Sala não encontrada.',
  professional_not_found: 'Dentista não encontrado.',
  email_already_registered: 'Este e-mail já tem acesso ao sistema.',
  last_admin_protected: 'Não é possível remover o último administrador.',
  cannot_remove_self: 'Você não pode remover a própria conta.',
  cannot_change_own_role: 'Você não pode alterar o seu próprio perfil.',
  user_not_found: 'Usuário não encontrado.',
  device_not_found: 'Dispositivo não encontrado.',
  device_not_paired: 'Esta TV ainda não foi pareada.',
  code_invalid_or_expired: 'Código inválido ou expirado. Gere um novo no painel.',
  invite_invalid_or_expired: 'Este convite expirou ou já foi usado. Peça um novo link.',
  sync_debounced: 'Sincronização feita há pouco. Aguarde um instante.',
  budgetExhausted: 'Limite de consultas ao Clinicorp atingido nesta hora.',
  circuitOpen: 'O Clinicorp está indisponível. Tentaremos novamente em alguns minutos.',
  invalid_body: 'Confira os dados informados.',
  invalid_query: 'Filtro inválido.',
  invalid_response_body: 'Resposta inesperada do servidor. Recarregue a página.',
  internal_error: 'Erro interno no servidor. Se persistir, acione o suporte.',
};

export function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    return MESSAGES[error.code] ?? `Falha na operação (${error.code}).`;
  }
  if (error instanceof Error && error.name === 'TypeError') {
    // `fetch` lança TypeError quando a rede cai — distinguir ajuda o diagnóstico.
    return 'Sem conexão com o servidor. Verifique a rede.';
  }
  return 'Falha inesperada. Recarregue a página.';
}

export function ErrorBlock({
  error,
  action,
}: {
  readonly error: unknown;
  readonly action?: ReactNode | undefined;
}): ReactNode {
  return (
    <div
      role="alert"
      className="flex flex-col gap-3 rounded-[var(--radius-card)] border border-severity-critical/40 bg-severity-critical/8 px-4 py-4"
    >
      <div className="flex items-start gap-2.5">
        <AlertTriangle
          aria-hidden="true"
          className="mt-0.5 size-5 shrink-0 text-severity-critical"
        />
        <p className="text-sm leading-relaxed text-ink">{describeError(error)}</p>
      </div>
      {action}
    </div>
  );
}

/** Aviso menos severo: operação segue possível, só o dado está velho. */
export function WarningBanner({ children }: { readonly children: ReactNode }): ReactNode {
  return (
    <div
      role="status"
      className="flex items-start gap-2.5 rounded-[var(--radius-control)] border border-severity-attention/50 bg-severity-attention/10 px-3.5 py-2.5 text-sm leading-relaxed text-ink"
    >
      <AlertTriangle
        aria-hidden="true"
        className="mt-0.5 size-4 shrink-0 text-severity-attention-ink"
      />
      <div>{children}</div>
    </div>
  );
}
